import { useCallback, useEffect, useState } from 'react';
import { Button, DatePicker, Input, List, Select } from 'antd';

import { useLocalData } from '../../../../../hooks/useLocalData';
import { useNotificationContext } from '../../../../../contexts/NotificationContext';
import { useTranslation } from 'react-i18next';

import fetcher from '../../../../../helpers/fetcher';
import { ENVIRONMENT_CONFIG } from '../../../../../constants/environmentConfig';
import { API_ENDPOINTS } from '../../../../../constants/apiEndpoints';
import ConfirmModelComponent from '../../../../../components/model/ConfirmationModelComponent';
import CreateNewTask from '../CreateNewTask/CreateNewTask';
import { getInspectorUsers } from '../../../../../api/services/userService';
import { duplicateTask, updateTask } from '../../../../../api/services/taskService';
import { useNavigate } from 'react-router-dom';
import { referenceDataSyncService } from '../../../../../services/referenceDataSyncService';
import { usePermissions } from '../../../../../hooks/usePermissions';

import Task from './Task';
import FormModelComponent from '../../../../../components/model/FormModelComponent';
import TaskCompletionForm from '../CreateNewTask/TaskCompletionForm';
import { XSquare } from 'react-bootstrap-icons';
import { useForm } from 'antd/es/form/Form';
import { useIsOnlineContext } from '../../../../../contexts/IsOnlineContext';
import { PermissionEnum } from '../../../../../enums/PermissionEnum';
import { Dayjs } from 'dayjs';
import { TaskTypeOption } from '../CreateNewTask/AddTaskForm';
import { TaskTypeEnum } from '../../../../../enums/TaskTypeEnum';
import { RoleName } from '../../../../../enums/RoleNameEnm';
import { localDataService } from '../../../../../services/localDataService';
import { fetchAndEnrichOfflineTasks } from '../../../../../helpers/offlineTaskHelpers';
import { TaskStatus } from '../../../timelineItem';

interface TaskListsProps {
  refreshTaskSection?: number;
}

const TaskLists: React.FC<TaskListsProps> = ({ refreshTaskSection }) => {
  const navigate = useNavigate();
  const { t } = useTranslation('task');
  const [taskData, setTaskData] = useState<any>([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalTasks, setTotalTasks] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(5);

  const { hasPermission } = usePermissions();

  const { successNotification, errorNotification } = useNotificationContext();

  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState<boolean>(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string>('');
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [selectedTaskData, setSelectedTaskData] = useState<any>(null);
  const [triggerEdit, setTriggerEdit] = useState(0);
  const [userData, setUserData] = useState<any[]>([]);

  const [isTaskCompleteModalOpen, setIsTaskCompleteModalOpen] = useState<boolean>(false);

  const [isLegalMetrologyTaskCompleteModalOpen, setIsLegalMetrologyTaskCompleteModalOpen] =
    useState<boolean>(false);
  const [taskCompleteForm] = useForm();

  // filter states
  const [selectedInspectorIds, setSelectedInspectorIds] = useState<string[]>([]);
  const [selectedDueDate, setSelectedDueDate] = useState<Dayjs | null>(null);
  const [taskNameFilter, setTaskNameFilter] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>();
  const [selectedTaskType, setSelectedTaskType] = useState<string | undefined>();
  const [selectedTaskTypeId, setSelectedTaskTypeId] = useState<number | undefined>();
  const [taskTypeOptions, setTaskTypeOptions] = useState<TaskTypeOption[]>([]);
  const [enableClearFiltersBtn, setEnableClearFiltersBtn] = useState<boolean>(false);
  const { isOnline } = useIsOnlineContext();
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const [editRefreshTaskSection, setEditRefreshTaskSection] = useState(0);

  const isInspector =
    hasPermission(PermissionEnum.RUN_INSPECTIONS) &&
    !hasPermission(PermissionEnum.CREATE_INSPECTIONS);
  const canFilterByInspector = !isInspector && isOnline;

  useEffect(() => {
    getUsers();
    getTaskType();
    getAllTaskList(1, pageSize, {});
  }, []);

  const editRefresh = () => {
    setEditRefreshTaskSection((prev) => prev + 1);
  };

  useEffect(() => {
    if (refreshTaskSection !== 0) {
      getAllTaskList(currentPage, pageSize, activeFilters);
    }
  }, [refreshTaskSection]);

  useEffect(() => {
    if (editRefreshTaskSection !== 0) {
      getAllTaskList(currentPage, pageSize, activeFilters);
    }
  }, [editRefreshTaskSection]);

  const {
    data: offlineData,
    total: offlineTotal,
    loading: offlineLoading,
    refresh: refreshOfflineQuery,
  } = useLocalData({
    entity: 'task',
    autoSync: false,
    page: currentPage,
    limit: pageSize,
    sortBy: 'taskName',
    sortOrder: 'asc',
    filters: activeFilters,
    search: taskNameFilter,
    searchFields: ['taskName'],
  });

  useEffect(() => {
    if (isOnline) return;

    let isMounted = true;
    setIsLoadingTasks(true);

    fetchAndEnrichOfflineTasks(offlineData)
      .then((enrichedTasks) => {
        if (isMounted) {
          setTaskData(enrichedTasks);
          setTotalTasks(offlineTotal);
        }
      })
      .catch((error) => {
        if (isMounted) {
          setTaskData(offlineData);
          setTotalTasks(offlineTotal);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoadingTasks(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [isOnline, offlineData, offlineTotal]);

  const getUsers = async () => {
    try {
      const response = await getInspectorUsers({
        roles: [RoleName.INSPECTOR, RoleName.AUDITOR],
      });

      if (response?.statusText === 'SUCCESS' && response?.data?.data) {
        const users: any = response?.data?.data;
        setUserData(users);
      } else {
        console.error('getUsers - invalid response format');
      }
    } catch (error) {}
  };

  const getTaskType = async () => {
    try {
      const response = await referenceDataSyncService.getTaskTypes();
      setTaskTypeOptions(response);
    } catch (error) {}
  };

  const getAssigneeNames = (assignees: string[] | string) => {
    try {
      if (!assignees || assignees.length === 0) {
        return 'Unassigned';
      }

      const assigneeIds = Array.isArray(assignees) ? assignees : [assignees];

      return assigneeIds
        .map((id) => {
          const user = userData.find((u) => u.id === id || u.userId === id || u.refId === id);

          if (user) {
            return user.userName || user.name || user.firstName || user.email || id;
          }

          return id;
        })
        .join(', ');
    } catch (error) {
      return Array.isArray(assignees) ? assignees.join(', ') : assignees;
    }
  };

  const getAllTaskListOnline = useCallback(
    async (page: number = currentPage, limit: number = pageSize, filters: any = {}) => {
      setIsLoadingTasks(true);
      try {
        const paginationParams = {
          page,
          limit,
          ...filters,
          timezoneOffset: new Date().getTimezoneOffset(),
        };

        const response = await fetcher.post(
          API_ENDPOINTS.TASK.GET_TASK_OVERVIEW,
          paginationParams,
          {},
          ENVIRONMENT_CONFIG.ANALYSIS_SERVICE
        );

        if (response?.statusText === 'SUCCESS' && response?.data?.data) {
          const tasks = response?.data?.data;
          const total = response?.data?.total || 0;
          setTaskData(tasks);
          setTotalTasks(total);
          setCurrentPage(page);

          try {
            await referenceDataSyncService.forceSyncEntity('task');
          } catch (syncError) {
            console.warn('Failed to sync tasks using existing mechanism:', syncError);
          }
        } else {
          console.error('Failed to fetch tasks from backend');
        }
      } catch (error) {
      } finally {
        setIsLoadingTasks(false);
      }
    },
    []
  );

  const getAllTaskList = useCallback(
    async (page = currentPage, limit = pageSize, filters: any = {}) => {
      if (!isOnline) return;

      await getAllTaskListOnline(page, limit, filters);
    },
    [isOnline, currentPage, pageSize, getAllTaskListOnline]
  );

  const buildFilterPayload = () => {
    const filterPayload: any = {};
    if (selectedInspectorIds && selectedInspectorIds.length > 0) {
      filterPayload.inspectorIds = selectedInspectorIds;
    }
    if (selectedDueDate) {
      filterPayload.dueDate = selectedDueDate.startOf('day').valueOf();
    }
    if (taskNameFilter && taskNameFilter.trim() !== '' && isOnline) {
      filterPayload.taskName = taskNameFilter.trim();
    }
    if (selectedStatus) {
      filterPayload.status = selectedStatus;
    }
    if (selectedTaskTypeId) {
      filterPayload.taskTypeId = selectedTaskTypeId;
    }

    return filterPayload;
  };

  useEffect(() => {
    const payload = buildFilterPayload();
    setActiveFilters(payload);
    setEnableClearFiltersBtn(Object.keys(payload).length > 0);
    setCurrentPage(1);
    getAllTaskList(1, pageSize, payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedInspectorIds,
    selectedDueDate,
    taskNameFilter,
    selectedStatus,
    pageSize,
    selectedTaskTypeId,
  ]);

  const handleClearFilters = () => {
    setSelectedTaskType(undefined);
    setSelectedInspectorIds([]);
    setSelectedDueDate(null);
    setTaskNameFilter('');
    setSelectedStatus(undefined);
    setActiveFilters({});
    setCurrentPage(1);
    getAllTaskList(1, pageSize, {});
    setSelectedTaskTypeId(undefined);
  };

  useEffect(() => {
    getAllTaskList();
  }, []);

  const getDeleteConfirmation = (id: string) => {
    setIsEdit(false);
    setIsDeleteConfirmationOpen(true);
    setSelectedTaskId(id);
  };

  const handleDelete = async () => {
    if (!hasPermission(PermissionEnum.DELETE_INSPECTIONS)) return;

    try {
      const selectedTask = taskData.find((task: any) => task?.refId === selectedTaskId);
      if (selectedTask) {
        await updateTask(selectedTaskId, {
          isActive: false,
        });
      }

      setIsDeleteConfirmationOpen(false);
      successNotification(t('taskDeleteSuccess'));
      getAllTaskList(1, pageSize, activeFilters);
    } catch (error) {
      setIsDeleteConfirmationOpen(false);
      errorNotification(t('taskDeleteFail'));
    }
  };

  const cancelDeleteConfirmation = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const handleEdit = (refId: string) => {
    setTriggerEdit((prev) => prev + 1);

    setSelectedTaskId(refId);

    const selectedTaskData = taskData.find((task: any) => task?.refId === refId);

    setSelectedTaskData(selectedTaskData);
    setIsEdit(true);
  };

  const handleMarkAsCompleted = (refId: string) => {
    setSelectedTaskId(refId);
    const selectedTaskData = taskData.find((task: any) => task?.refId === refId);

    if (selectedTaskData?.taskTypeKey === TaskTypeEnum.LEGAL_METROLOGY_INSPECTION) {
      setIsLegalMetrologyTaskCompleteModalOpen(true);
    } else {
      setIsTaskCompleteModalOpen(true);
    }

    setSelectedTaskData(selectedTaskData);

    taskCompleteForm.resetFields();
  };

  const handleDuplicate = async (id: number) => {
    if (!isOnline || !hasPermission(PermissionEnum.CREATE_INSPECTIONS)) return;
    if (!Number.isInteger(id)) return;

    try {
      await duplicateTask(id);
      successNotification(t('taskDuplicateSuccess'));
      getAllTaskList(currentPage, pageSize, activeFilters);
    } catch (error) {
      errorNotification(t('taskDuplicateFail'));
    }
  };

  const handleCancel = () => {
    taskCompleteForm.resetFields();
    setIsTaskCompleteModalOpen(false);
    setIsLegalMetrologyTaskCompleteModalOpen(false);
  };

  const completeTask = async () => {
    const completeTaskFormData = taskCompleteForm.getFieldsValue();

    await taskCompleteForm.validateFields();

    const body = {
      respondent: completeTaskFormData?.respondent,
      signatureInitial: completeTaskFormData?.signatureInitial,
      remark: completeTaskFormData?.remark,
      signature: completeTaskFormData?.signature,
      status: 'completed',
    };

    setIsTaskCompleteModalOpen(false);
    setIsLegalMetrologyTaskCompleteModalOpen(false);

    try {
      const selectedTask = selectedTaskData;
      if (selectedTask) {
        const updatedTaskData = {
          //  ...selectedTask,
          ...body,
          refId: selectedTaskId,
          updateTime: Date.now(),
        };

        let result;
        if (!isOnline) {
          // ---- when offline add to sync queue update via indexedDB ----
          result = await localDataService.update('task', selectedTaskId, updatedTaskData);
        } else {
          // ---- when online ----
          result = await updateTask(selectedTaskId, updatedTaskData);
        }

        if (result) {
          taskCompleteForm.resetFields();

          // Update task status
          setTaskData((prevTasks: any[]) =>
            prevTasks.map((taskItem: any) =>
              taskItem?.refId === selectedTaskId || taskItem?.id === selectedTaskId
                ? { ...taskItem, ...body, status: TaskStatus.COMPLETED, updateTime: Date.now() }
                : taskItem
            )
          );

          if (isOnline) {
            successNotification(t('taskCompletionSuccess'), { duration: 10 });
            getAllTaskList(currentPage, pageSize, activeFilters);
          } else {
            successNotification(t('taskCompletionSuccessOffline'), { duration: 10 });
            refreshOfflineQuery();
          }
          setIsTaskCompleteModalOpen(false);
          setIsLegalMetrologyTaskCompleteModalOpen(false);
        }
      }
    } catch (error) {
      setIsTaskCompleteModalOpen(false);
      setIsLegalMetrologyTaskCompleteModalOpen(false);
      errorNotification(t('taskCompletionFail'));
    }
  };

  const handlePageChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page);
    if (newPageSize && newPageSize !== pageSize) {
      setPageSize(newPageSize);
      getAllTaskList(1, newPageSize, activeFilters);
    } else {
      getAllTaskList(page, pageSize, activeFilters);
    }
  };

  const onTaskTypeChange = (value: any, option: any) => {
    setSelectedTaskType(value);
    setSelectedTaskTypeId(option?.id);
  };

  return (
    <div className="responsive-list-padding">
      <ConfirmModelComponent
        isOpen={isDeleteConfirmationOpen}
        onClickedCancel={cancelDeleteConfirmation}
        header={t('confirmDeleteTitle')}
        content={<p>{t('confirmDeleteMessage')}</p>}
        footerAction={{
          confimation: {
            label: t('confirm'),
            onClick: handleDelete,
          },
          cancel: {
            label: t('cancel'),
            onClick: cancelDeleteConfirmation,
          },
        }}
        styleClasses="z-100"
      />

      <ConfirmModelComponent
        isOpen={isLegalMetrologyTaskCompleteModalOpen}
        onClickedCancel={handleCancel}
        header={t('taskCompleteTitle')}
        content={<p>{t('taskCompleteContent')}</p>}
        footerAction={{
          confimation: {
            label: t('yes'),
            onClick: completeTask,
          },
          cancel: {
            label: t('no'),
            onClick: handleCancel,
          },
        }}
        styleClasses="z-100"
      />

      <FormModelComponent
        isOpen={isTaskCompleteModalOpen}
        header={t('taskCompleteTitle')}
        content={
          <TaskCompletionForm
            form={taskCompleteForm}
            handleComplete={completeTask}
            handleCancel={handleCancel}
          />
        }
        onClickedCancel={handleCancel}
        footerAction={{
          confimation: {
            label: t('completeTask'),

            onClick: completeTask,
          },
          cancel: {
            label: t('cancel'),

            onClick: handleCancel,
          },
        }}
        closeIcon={<XSquare className="text-primary-font-red" size={20} />}
        contentStyles="mb-4 px-2 max-h-[80vh] overflow-y-auto space-y-2"
      ></FormModelComponent>

      {isEdit && (
        <CreateNewTask
          selectedTaskId={selectedTaskId}
          selectedTaskData={selectedTaskData}
          isEditClicked={true}
          triggerEdit={triggerEdit}
          hideCreateBtn={true}
          // refreshTasks={getAllTaskList}
          refreshTasks={editRefresh}
        />
      )}
      <div className="mb-6">
        <div className="mx-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm md:mx-6">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="text-lg font-semibold">{t('filters')}</h3>
            <Button
              type="link"
              onClick={handleClearFilters}
              className="hover:text-primary-dark text-sm text-primary"
              disabled={!enableClearFiltersBtn}
            >
              {t('clearFilters')}
            </Button>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {/* Inspector – only SA/Admin/Analyst */}
            {canFilterByInspector && (
              <div className="flex flex-col justify-between gap-1">
                <span className="truncate text-sm font-medium text-gray-700" title={t('inspector')}>
                  {t('inspector')}
                </span>
                <Select
                  mode="multiple"
                  className="w-full [&>.ant-select-selector]:!min-h-[38px] [&>.ant-select-selector]:!rounded-lg"
                  placeholder={t('selectInspector')}
                  allowClear
                  value={selectedInspectorIds}
                  onChange={(value) => setSelectedInspectorIds(value)}
                  maxTagCount="responsive"
                  maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} ${t('more')}`}
                  options={userData.map((u) => ({
                    value: u.id || u.userId || u.refId,
                    label: u.userName || u.name || u.firstName || u.email,
                  }))}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                  }
                />
              </div>
            )}

            {/* Task Due Date */}
            <div className="flex flex-col justify-between gap-1">
              <span className="truncate text-sm font-medium text-gray-700" title={t('taskDueDate')}>
                {t('taskDueDate')}
              </span>
              <DatePicker
                className="!h-[38px] w-full [&.ant-picker]:!h-[38px] [&.ant-picker]:!rounded-lg"
                placeholder={t('taskDueDate')}
                value={selectedDueDate}
                onChange={(value) => setSelectedDueDate(value)}
                allowClear
              />
            </div>

            {/* Task Name */}
            <div className="flex flex-col justify-between gap-1">
              <span className="truncate text-sm font-medium text-gray-700" title={t('taskName')}>
                {t('taskName')}
              </span>
              <Input
                className="!h-[38px] w-full [&.ant-input-affix-wrapper]:!h-[38px] [&.ant-input-affix-wrapper]:!rounded-lg [&.ant-input]:!h-full"
                placeholder={t('searchByTaskName')}
                value={taskNameFilter}
                onChange={(e) => setTaskNameFilter(e.target.value)}
                allowClear
              />
            </div>

            {/* Status */}
            <div className="flex flex-col justify-between gap-1">
              <span className="truncate text-sm font-medium text-gray-700" title={t('status')}>
                {t('status')}
              </span>
              <Select
                className="w-full [&>.ant-select-selector]:!h-[38px] [&>.ant-select-selector]:!min-h-[38px] [&>.ant-select-selector]:!rounded-lg"
                placeholder={t('selectStatus')}
                allowClear
                value={selectedStatus}
                onChange={(value) => setSelectedStatus(value)}
                options={[
                  { value: 'pending', label: t('Pending') },
                  { value: 'in_progress', label: t('InProgress') },
                  { value: 'completed', label: t('Completed') },
                ]}
              />
            </div>

            {/* Task Type */}
            <div className="flex flex-col justify-between gap-1">
              <span className="truncate text-sm font-medium text-gray-700" title={t('taskType')}>
                {t('taskType')}
              </span>
              <Select
                className="w-full [&>.ant-select-selector]:!h-[38px] [&>.ant-select-selector]:!min-h-[38px] [&>.ant-select-selector]:!rounded-lg"
                placeholder={t('taskTypePlaceholder')}
                allowClear
                value={selectedTaskType}
                onChange={onTaskTypeChange}
                options={
                  taskTypeOptions &&
                  taskTypeOptions?.map((type) => ({
                    label: t(`taskTypeEnum.${type?.key}`),
                    value: type?.key,
                    id: type?.id,
                  }))
                }
              />
            </div>
            <div className="hidden xl:block" />
          </div>
        </div>
      </div>
      <List
        pagination={{
          position: 'bottom',
          align: 'start',
          pageSize: pageSize,
          current: currentPage,
          total: totalTasks,
          showSizeChanger: true,
          showQuickJumper: true,
          pageSizeOptions: ['2', '5', '10'],
          showTotal: (total, range) => `${range[0]}-${range[1]} ${t('of')} ${total} ${t('tasks')}`,
          onChange: handlePageChange,
          onShowSizeChange: handlePageChange,
          locale: {
            jump_to: t('goTo'),
            page: t('page'),
            items_per_page: `/ ${t('page')}`,
          },
          className: 'mx-3 md:mx-4',
          size: 'small',
        }}
        split={false}
        loading={isLoadingTasks}
        dataSource={taskData}
        renderItem={(item: any, index) => (
          <List.Item key={item?.taskName + index}>
            <Task
              item={item}
              handleEdit={handleEdit}
              getDeleteConfirmation={getDeleteConfirmation}
              handleMarkAsCompleted={handleMarkAsCompleted}
              handleDuplicate={handleDuplicate}
              isOnline={isOnline}
            />
          </List.Item>
        )}
      />
    </div>
  );
};

export default TaskLists;
